import { CANONICAL_EXPERIENCE_REGISTRY } from '../experience/canonical-registry.js';
const PLATFORM_PERSONAS = new Set([
    'PLATFORM_ROOT',
    'PLATFORM_ADMIN',
    'PLATFORM_USER',
]);
const ORGANIZATION_PERSONAS = new Set([
    'ORG_ADMIN',
    'ORG_USER',
    'ORG_CLIENT',
    'SUPPORT_DELEGATE',
]);
/**
 * Classify a route for composition filtering.
 *
 * Routes with explicit `allowedPersonas` are classified by persona family.
 * Routes without `allowedPersonas` (authorization handled by the operation
 * guard) are classified by routeId prefix: `platform-*` routes are
 * platform-only; all others are shared (included in both compositions).
 */
function routeBelongsToComposition(route, composition) {
    if (route.authentication === 'public')
        return true;
    if (composition === 'COMBINED')
        return true;
    const personas = route.allowedPersonas;
    if (personas && personas.length > 0) {
        const hasPlatform = personas.some((p) => PLATFORM_PERSONAS.has(p));
        const hasOrganization = personas.some((p) => ORGANIZATION_PERSONAS.has(p));
        if (composition === 'PLATFORM_CONTROL_PLANE')
            return hasPlatform;
        return hasOrganization;
    }
    // No explicit persona restriction — classify by routeId prefix.
    const isPlatformRoute = String(route.routeId).startsWith('platform-');
    if (composition === 'PLATFORM_CONTROL_PLANE')
        return isPlatformRoute;
    // Organization runtime: include non-platform routes (shared + org-only).
    return !isPlatformRoute;
}
function filterExperienceRoutes(routes, composition) {
    if (composition === 'COMBINED')
        return [...routes];
    return Object.freeze(routes.filter((r) => routeBelongsToComposition(r, composition)));
}
function isPlatformResource(resource) {
    return resource.authority === 'PLATFORM';
}
function filterResources(resources, composition) {
    if (composition === 'COMBINED')
        return resources;
    const wantPlatform = composition === 'PLATFORM_CONTROL_PLANE';
    const filtered = {};
    for (const [id, resource] of Object.entries(resources)) {
        const isPlatform = isPlatformResource(resource);
        if (wantPlatform === isPlatform)
            filtered[id] = resource;
    }
    return Object.freeze(filtered);
}
/** Table/form IDs referenced by any resource in the filtered set. */
function referencedDescriptorIds(resources) {
    const tableIds = new Set();
    const formIds = new Set();
    for (const resource of Object.values(resources)) {
        if (resource.tableDescriptorId)
            tableIds.add(resource.tableDescriptorId);
        for (const formId of [
            resource.createFormDescriptorId,
            resource.editFormDescriptorId,
            resource.viewFormDescriptorId,
        ]) {
            if (formId)
                formIds.add(formId);
        }
    }
    return { tableIds, formIds };
}
function isPlatformDescriptorId(id) {
    const lower = id.toLowerCase();
    return lower.includes('platform');
}
function filterTables(tables, composition, referencedTableIds) {
    if (composition === 'COMBINED')
        return tables;
    const wantPlatform = composition === 'PLATFORM_CONTROL_PLANE';
    const filtered = {};
    for (const [id, table] of Object.entries(tables)) {
        // Derive ownership from the referencing resource when possible.
        if (referencedTableIds.has(id)) {
            // Only include if at least one referencing resource is in the filtered set.
            // referencedTableIds was built from the already-filtered resources, so
            // any referenced table is owned by this composition.
            filtered[id] = table;
            continue;
        }
        // Standalone table — fall back to ID-prefix classification.
        if (wantPlatform === isPlatformDescriptorId(id))
            filtered[id] = table;
    }
    return Object.freeze(filtered);
}
function filterForms(forms, composition, referencedFormIds) {
    if (composition === 'COMBINED')
        return forms;
    const wantPlatform = composition === 'PLATFORM_CONTROL_PLANE';
    const filtered = {};
    for (const [id, form] of Object.entries(forms)) {
        if (referencedFormIds.has(id)) {
            filtered[id] = form;
            continue;
        }
        if (wantPlatform === isPlatformDescriptorId(id))
            filtered[id] = form;
    }
    return Object.freeze(filtered);
}
function filterPersonas(personas, composition) {
    if (composition === 'COMBINED')
        return personas;
    const family = composition === 'PLATFORM_CONTROL_PLANE'
        ? PLATFORM_PERSONAS
        : ORGANIZATION_PERSONAS;
    const filtered = {};
    for (const [persona, policy] of Object.entries(personas)) {
        if (family.has(persona)) {
            filtered[persona] = policy;
        }
    }
    return Object.freeze(filtered);
}
function collectActions(tables, forms) {
    const actions = {};
    for (const table of Object.values(tables)) {
        for (const action of [
            ...(table.rowActions ?? []),
            ...(table.bulkActions ?? []),
            ...(table.pageActions ?? []),
            ...(table.createAction ? [table.createAction] : []),
            ...(table.rowActivationAction ? [table.rowActivationAction] : []),
        ])
            actions[action.id] = action;
    }
    for (const form of Object.values(forms)) {
        for (const action of form.actions)
            actions[action.id] = action;
    }
    return Object.freeze(actions);
}
/**
 * Build a composition-specific experience registry view from the canonical
 * (combined) registry. The returned registry is structurally limited to the
 * descriptors owned by the target application composition.
 */
export function buildExperienceRegistryForComposition(canonical, composition) {
    if (composition === 'COMBINED')
        return canonical;
    const resources = filterResources(canonical.resources, composition);
    const { tableIds, formIds } = referencedDescriptorIds(resources);
    const tables = filterTables(canonical.tables, composition, tableIds);
    const forms = filterForms(canonical.forms, composition, formIds);
    const routes = filterExperienceRoutes(canonical.routes, composition);
    const personas = filterPersonas(canonical.personas, composition);
    const actions = collectActions(tables, forms);
    return Object.freeze({
        schemaVersion: canonical.schemaVersion,
        registryVersion: canonical.registryVersion,
        routes,
        resources,
        tables,
        forms,
        actions,
        personas,
        reports: canonical.reports,
        dashboards: canonical.dashboards,
        financialCapabilities: canonical.financialCapabilities,
        financialReportTaxonomy: canonical.financialReportTaxonomy,
    });
}
/** Experience registry containing only Organization-runtime-owned descriptors. */
export const ORGANIZATION_EXPERIENCE_REGISTRY = buildExperienceRegistryForComposition(CANONICAL_EXPERIENCE_REGISTRY, 'ORGANIZATION_RUNTIME');
/** Experience registry containing only Platform-control-plane-owned descriptors. */
export const PLATFORM_EXPERIENCE_REGISTRY = buildExperienceRegistryForComposition(CANONICAL_EXPERIENCE_REGISTRY, 'PLATFORM_CONTROL_PLANE');
/**
 * Select the appropriate experience registry for a deployment composition.
 * Throws for unsupported compositions.
 */
export function getExperienceRegistryForComposition(composition) {
    switch (composition) {
        case 'ORGANIZATION_RUNTIME':
            return ORGANIZATION_EXPERIENCE_REGISTRY;
        case 'PLATFORM_CONTROL_PLANE':
            return PLATFORM_EXPERIENCE_REGISTRY;
        case 'COMBINED':
            return CANONICAL_EXPERIENCE_REGISTRY;
        default:
            throw new Error(`Unsupported application composition: ${composition}`);
    }
}
// ── Re-export classification helpers for structural absence tests ──────────
export function isPlatformResourceDescriptor(resource) {
    return isPlatformResource(resource);
}
export function platformResourceIds(resources) {
    return Object.keys(resources).filter((id) => isPlatformResource(resources[id]));
}
export function organizationResourceIds(resources) {
    return Object.keys(resources).filter((id) => !isPlatformResource(resources[id]));
}
export function platformRouteIds(routes) {
    return routes
        .filter((route) => route.authentication !== 'public')
        .filter((route) => {
        const personas = route.allowedPersonas;
        if (personas && personas.length > 0) {
            return personas.some((persona) => PLATFORM_PERSONAS.has(persona));
        }
        return String(route.routeId).startsWith('platform-');
    })
        .map((route) => String(route.routeId));
}
export function organizationRouteIds(routes) {
    return routes
        .filter((route) => route.authentication !== 'public')
        .filter((route) => {
        const personas = route.allowedPersonas;
        if (personas && personas.length > 0) {
            return personas.some((persona) => ORGANIZATION_PERSONAS.has(persona));
        }
        return !String(route.routeId).startsWith('platform-');
    })
        .map((route) => String(route.routeId));
}
